import * as Sentry from '@sentry/vue';
import { createPinia } from 'pinia';
import { createApp } from 'vue';

import { createFiltersConfig } from '~/config/search/filters';
import { createPreferredCars } from '~/config/search/preferredCars';
import { sentryBaseConfig } from '~/config/sentry';

import { useServiceContainer, useStoreContainer } from '~/container';

import EtLoadingSpinnerCircular from '~/modules/core/components/EtLoadingSpinnerCircular.vue';
import EtNavigation from '~/modules/core/components/EtNavigation.vue';
import EtSimpleDataSelector from '~/modules/core/components/EtSimpleDataSelector.vue';

import App from './App';
import deepAssign from '../lib/deep-assign';
import immutableUpdate from '../lib/immutable-update';
import structuredCloneAlternative from '../lib/structured-clone-alternative';

import loadVueTemplates from '~/globals/loadVueTemplates';
import { ClickOutside, DynamicText, ExitIntent, OnSrcError, Sticky, WindowFocus } from '~/modules/core/directives';
import { buildErrorData } from '~/modules/core/errors';
import { CustomerSupportModalHandler } from '~/modules/customerService/directives';
import { FocusFieldEvent, FocusFieldTrigger, ScrollTopElement } from '~/modules/form/directives';
import { InfiniteScroll, SmartSticky } from '~/modules/search/directives';

window.deepClone = structuredCloneAlternative;
window.deepAssign = deepAssign;
window.immutableUpdate = immutableUpdate;

const components = {
    'et-loading-spinner-circular': EtLoadingSpinnerCircular,
    'et-simple-data-selector': EtSimpleDataSelector,
    'et-navigation': EtNavigation,
};

const app = createApp(App);

// Register all needed components which are not imported in through page components
Object.entries(components).forEach(([componentName, component]) => {
    app.component(componentName, component);
});

app.use(createPinia());

useStoreContainer().AppStore.setAppInstance(app);

Promise.all([import('./router'), loadVueTemplates('main')]).then(([response]) => {
    const router = response.default;

    app.use(router);

    app.directive('scrollTopElement', ScrollTopElement);
    app.directive('focusFieldTrigger', FocusFieldTrigger);
    app.directive('focusFieldEvent', FocusFieldEvent);
    app.directive('dynamicText', DynamicText);
    app.directive('onSrcError', OnSrcError);
    app.directive('sticky', Sticky);
    app.directive('smartSticky', SmartSticky);
    app.directive('infiniteScroll', InfiniteScroll);
    app.directive('customerSupportModalHandler', CustomerSupportModalHandler);
    app.directive('exitIntent', ExitIntent);
    app.directive('windowFocus', WindowFocus);
    app.directive('clickOutside', ClickOutside);

    createFiltersConfig();
    createPreferredCars();

    // Doesn't work if adding to vite config
    app.config.compilerOptions.isCustomElement = (tag) => ['vue-template', 'vr'].includes(tag);

    app.config.errorHandler = (error, _instance, info) => {
        useServiceContainer().BackendLoggerService.log(
            'error',
            'Uncaught error occurred in Vue',
            buildErrorData(error, { origin: 'main', info }),
        );
    };

    window.onerror = (event: Event | string, source?: string, lineno?: number, colno?: number, error?: Error) => {
        useServiceContainer().BackendLoggerService.log(
            'error',
            'Uncaught error occurred',
            buildErrorData(error ?? event, { origin: 'main', source, lineno, colno }),
        );
    };

    Sentry.init({
        ...sentryBaseConfig,
        app,
        integrations: [
            Sentry.browserTracingIntegration({
                router,
                routeLabel: 'path',
            }),
        ],
    });

    app.mount('#app');
});
